<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAcceptsChat = useMapGetter('getCurrentUserAcceptsChat');
const authUIFlags = useMapGetter('getAuthUIFlags');

const { isImpersonating } = useImpersonation();

// Availability/accepts_chat fix — keep computed flags off the store so the
// template can disable individual controls while a request is in flight.
// Defaults are false so initial render doesn't show anything as disabled.
const isUpdatingAvailability = computed(
  () => !!authUIFlags.value?.isUpdatingAvailability
);
const isUpdatingAcceptsChat = computed(
  () => !!authUIFlags.value?.isUpdatingAcceptsChat
);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const allAvailabilityStatuses = computed(() =>
  [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ].map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const availabilityStatuses = computed(() =>
  allAvailabilityStatuses.value.filter(status => status.value !== 'busy')
);

const activeStatus = computed(() => {
  return (
    allAvailabilityStatuses.value.find(status => status.active) ||
    availabilityStatuses.value[0]
  );
});

const acceptsChatToggle = computed({
  get: () => currentUserAcceptsChat.value !== false,
  set: async acceptsChat => {
    // Fix D — match the availability dropdown's behavior so impersonate errors
    // surface instead of silently failing at the backend.
    if (isImpersonating.value) {
      useAlert(t('PROFILE_SETTINGS.FORM.ACCEPTS_CHAT.IMPERSONATING_ERROR'));
      return;
    }
    if (isUpdatingAcceptsChat.value) return;

    try {
      // Fix B — must await so the dispatch's Promise rejection actually flows
      // into our catch and triggers the toast. The previous fire-and-forget
      // pattern combined with auth.js swallowing errors meant every failure
      // was invisible to the user.
      await store.dispatch('updateAcceptsChat', {
        accountId: currentAccountId.value,
        acceptsChat,
      });
    } catch (error) {
      useAlert(t('PROFILE_SETTINGS.FORM.ACCEPTS_CHAT.ERROR'));
    }
  },
});

async function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  if (isUpdatingAvailability.value) return;

  try {
    // Fix B — `await` is required so a rejected dispatch hits this catch.
    // The previous synchronous try/catch couldn't see async errors at all.
    await store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection>
    <div class="grid gap-0 wlab-profile-menu-status">
      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          <span class="wlab-pm-status-title">{{
            $t('SIDEBAR.SET_YOUR_AVAILABILITY')
          }}</span>
        </div>
        <DropdownContainer :teleport="false">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              class="min-w-[96px] wlab-pm-availability-btn"
              :class="{
                'opacity-60 pointer-events-none': isUpdatingAvailability,
              }"
              icon="i-lucide-chevron-down"
              trailing-icon
              :disabled="isUpdatingAvailability"
              @click.stop="toggle"
            >
              <div class="flex gap-1 items-center flex-grow text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span>{{ activeStatus.label }}</span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20 js-nested-dropdown">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              :class="{
                'opacity-60 pointer-events-none': isUpdatingAvailability,
              }"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>

      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          <span class="wlab-pm-status-title">Accepts chat</span>
        </div>
        <ToggleSwitch
          v-model="acceptsChatToggle"
          :disabled="isUpdatingAcceptsChat"
        />
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
